import type { CityResult, TodayNotificationsResponse } from "@ishvaram/api-client";

import { apiRequest, sameOriginRequest } from "./_fetch";

export type PlaceResult = CityResult;
export type { TodayNotificationsResponse };

// /_places/* is served by the ishvaram-content CF Worker against a D1
// cities table — see business/ishvaram/apps/web/edge-db/worker/places.ts
// and rule 34. Same-origin so no CORS preflight; sub-30ms typical
// response from the user's nearest POP.
export async function searchPlaces(query: string, limit = 8): Promise<PlaceResult[]> {
  if (query.length < 2) return [];
  const params = new URLSearchParams({ q: query, limit: String(limit) });
  try {
    return await sameOriginRequest<PlaceResult[]>(`/_places/autocomplete?${params}`);
  } catch {
    return [];
  }
}

/**
 * Network-aware place search for the submit-time typed-city resolver.
 *
 * `searchPlaces` above SWALLOWS a network error into `[]` — which is
 * indistinguishable from "city genuinely not found". That single conflation is
 * the root cause of the 15,124 birthPlace failures: a flaky-network lookup and a
 * misspelled city both surfaced as the SAME "pick from the dropdown" error, so
 * we could never tell ambiguity from a true network fault. This variant returns
 * a discriminated result so the caller maps `network_error` → a retry card and
 * `empty`/`ok` → a "did you mean" chip, and the field funnel records WHICH it was.
 */
export type PlaceSearchResult =
  | { status: "ok"; results: PlaceResult[] }
  | { status: "network_error"; cause: string };

export async function searchPlacesResult(query: string, limit = 5): Promise<PlaceSearchResult> {
  if (query.trim().length < 2) return { status: "ok", results: [] };
  const params = new URLSearchParams({ q: query, limit: String(limit) });
  try {
    const results = await sameOriginRequest<PlaceResult[]>(`/_places/autocomplete?${params}`);
    return { status: "ok", results };
  } catch (err) {
    return { status: "network_error", cause: err instanceof Error ? err.message : String(err) };
  }
}

export async function getTodayNotifications(): Promise<TodayNotificationsResponse> {
  return apiRequest<TodayNotificationsResponse>("/transits/notifications/today");
}
